import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CaptionAudit,
  type Edl,
  type FatalLintCode,
  parseEdl,
  type EditCommand,
  type ShortsParams,
  DEFAULT_SHORTS_PARAMS,
  TARGET_DURATION_MAX_SEC,
  TARGET_DURATION_MIN_SEC,
} from '@repo/editor-core';
import { hasToken } from '@/lib/auth';
import { client } from '@/lib/rpc';
import { type ChatMessage, streamAgentChat } from '@/lib/agent-chat';
import { notifyExportStarted } from '@/lib/opener';
import { EditorShell } from '@/editor/EditorShell';
import type { AgentChatApi } from '@/editor/AgentPanel';
import type { GifSearch } from '@/editor/GifSearchModal';
import type { ShortsBranding } from '@/editor/branding';
import { useToast } from '@repo/ui';
import { apiErrorMessage, humanError } from '@/lib/api-error';

/** Resolve a highlight clip key to a playable URL via the ownership-checked proxy. */
function useProxyResolver() {
  return useCallback(async (assetId: string): Promise<string> => {
    // GIF overlays carry a fully-resolved external URL (allowlisted KLIPY host)
    // as their assetId — play it directly, no proxy ownership check applies.
    if (/^https?:\/\//.test(assetId)) return assetId;
    const res = await client.editor.assets.proxy.$get({ query: { key: assetId } });
    const json = await res.json();
    if (!res.ok || !('url' in json)) throw new Error(`asset unavailable: ${assetId}`);
    return json.url;
  }, []);
}

/** What the editor needs to open a project: the timeline plus its AI transcript. */
type ProjectData = {
  edl: Edl;
  title: string;
  status: string;
  chat: ChatMessage[];
  /** 소스 클립 길이(초). 우측 트림 상한 계산에 쓴다. */
  sourceDurationSec?: number;
  /** «어디가 터지는지» 근거가 있는가. 없으면 길이 자르기·맛보기 버튼을 안 띄운다. */
  hasBeats: boolean;
  /** 자막 자기검사 — 규칙이 결과를 몇 번 고쳤나. 전사가 없으면 null. */
  captionAudit: CaptionAudit | null;
};

/**
 * Export button suffix. The tab normally closes right after the render starts,
 * so this is the fallback surface — show a readable state, never the raw server
 * enum.
 */
const EXPORT_LABEL: Record<string, string> = {
  rendering: '내보내는 중…',
  rendered: '완료',
  failed: '실패',
};

/**
 * 서버 린트 코드를 사용자가 읽을 수 있는 한 줄로.
 *
 * `Record<FatalLintCode, …>` 라 **익스포트를 막는 코드는 전부 여기 있어야 한다** —
 * 새 코드를 추가하고 문구를 안 쓰면 컴파일이 깨진다. 안 그러면 모델용 영문 메시지가
 * (`clip "clip-1" runs to 142.30s but the source is 135.00s …`) 그대로 노출된다.
 */
const PROBLEM_LABEL: Record<FatalLintCode, string> = {
  video_gap: '영상 트랙에 빈 구간이 있어요. 클립을 붙여 주세요.',
  video_overlap: '영상 클립이 서로 겹쳐 있어요.',
  duration_mismatch: '클립 길이와 소스 구간이 어긋났어요.',
  empty_timeline: '영상 클립이 없어요.',
  caption_past_video: '영상이 끝난 뒤에 남은 자막이 있어요. 지우거나 앞으로 옮겨 주세요.',
  sfx_past_video: '영상이 끝난 뒤에 남은 효과음이 있어요. 지우거나 앞으로 옮겨 주세요.',
  overlay_past_video: '얹은 화면(얼굴·GIF)이 영상보다 길어요. 영상 끝에 맞춰 줄여 주세요.',
  source_out_of_range: '클립이 원본 영상보다 뒤까지 잡혀 있어요. 끝을 원본 안으로 줄여 주세요.',
};

function describeProblem(p: unknown): string {
  const o = p as { code?: string } | null;
  const code = typeof o?.code === 'string' ? o.code : '';
  // 서버 message 로 흘리지 않는다 — 그건 모델에게 하는 영문이다(위 주석).
  return PROBLEM_LABEL[code as FatalLintCode] ?? '타임라인에 문제가 있어요. 클립을 확인해 주세요.';
}

export default function EditorPage() {
  /**
   * 편집 리비전 — 「내가 불러온 뒤로 서버가 바뀌었나」를 판정하는 값.
   *
   * 🔴 서버가 EDL 을 쓰는 **모든** 응답에서 갱신해야 한다. 하나라도 빠뜨리면 자기가
   * 만든 변경 때문에 다음 저장이 「다른 창에서 먼저 저장했어요」로 잘못 막힌다 —
   * 거짓 경고는 조용한 덮어쓰기만큼이나 빠르게 신뢰를 깎는다.
   */
  const revRef = useRef<string | null>(null);
  const takeRev = useCallback((json: unknown): void => {
    const rev = (json as { rev?: unknown } | null)?.rev;
    if (typeof rev === 'string') revRef.current = rev;
  }, []);

  const { projectId = '' } = useParams();
  const resolver = useProxyResolver();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    enabled: hasToken() && Boolean(projectId),
    // The transcript rides along with the project so the AI panel can resume the
    // saved conversation on reload (the agent keeps its context with it).
    queryFn: async (): Promise<ProjectData> => {
      const res = await client.editor.projects[':id'].$get({ param: { id: projectId } });
      const json = await res.json();
      if (!res.ok || !('project' in json)) {
        throw new Error(
          apiErrorMessage(
            res.status,
            json,
            '프로젝트를 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.'
          )
        );
      }
      /*
       * 🔴 zod 에러를 그대로 올리면 안 된다. `ZodError.message` 는 이슈 배열을
       * JSON 으로 찍은 문자열이라, 아래 에러 화면에 이런 게 그대로 나왔다:
       *   `[ { "code": "invalid_value", "values": [1], "path": ["version"] … } ]`
       * (2026-09-11 점검 I2·I3 — EDL 버전이 다르거나 아예 null 로 왔을 때).
       */
      let edl: Edl;
      try {
        edl = parseEdl(json.project.edl);
      } catch {
        throw new Error(
          '타임라인을 읽지 못했어요. 새로고침해도 같으면 스트리머 대시보드에서 다시 만들어 주세요.'
        );
      }
      takeRev(json);
      return {
        edl,
        sourceDurationSec:
          'sourceDurationSec' in json && typeof json.sourceDurationSec === 'number'
            ? json.sourceDurationSec
            : undefined,
        title: json.project.title,
        status: json.project.status,
        hasBeats: 'hasBeats' in json ? Boolean(json.hasBeats) : false,
        captionAudit: 'captionAudit' in json ? (json.captionAudit ?? null) : null,
        chat: json.chat,
      };
    },
    // The panel restores the transcript once, at mount — refetching it behind a
    // live conversation would only risk clobbering what's on screen.
    refetchOnWindowFocus: false,
  });

  // Common branding (per-streamer): logo/channel/CTA overlays for the preview.
  const brandingQuery = useQuery({
    queryKey: ['shorts-branding'],
    enabled: hasToken(),
    queryFn: async (): Promise<ShortsBranding> => {
      const res = await client.editor.branding.$get();
      const json = await res.json();
      if (!res.ok || !('branding' in json)) throw new Error('branding load failed');
      const b = json.branding;
      return { platform: b.platform, channelName: b.channelName, ctaText: b.ctaText };
    },
  });

  /**
   * 웹캠 위치가 등록됐는가. 프리뷰 오버레이 타입(`ShortsBranding`)과 섞지 않는다 —
   * 그건 «화면에 그릴 것»이고 이건 «설정됐는가»라 수명이 다르다.
   */
  const webcamQuery = useQuery({
    queryKey: ['shorts-webcam'],
    enabled: hasToken(),
    queryFn: async (): Promise<boolean> => {
      const res = await client.editor.branding.$get();
      const json = await res.json();
      if (!res.ok || !('settings' in json)) return false;
      return Boolean(json.settings?.webcamRect);
    },
  });

  // ── 편집 설정 (D1: «어떻게 만들지»를 대화가 아니라 필드로) ──────────────
  const paramsQuery = useQuery({
    queryKey: ['project-params', projectId],
    enabled: hasToken() && Boolean(projectId),
    queryFn: async (): Promise<ShortsParams> => {
      const res = await client.editor.projects[':id'].params.$get({ param: { id: projectId } });
      const json = await res.json();
      if (!res.ok || !('params' in json)) throw new Error('설정을 불러오지 못했습니다');
      return json.params as ShortsParams;
    },
  });

  /** 프리셋별 «몇 곳 · 몇 초». 적용 전에 보여줘야 무엇을 고를지 판단이 선다. */
  const silenceQuery = useQuery({
    queryKey: ['project-silence', projectId],
    enabled: hasToken() && Boolean(projectId),
    queryFn: async () => {
      const res = await client.editor.projects[':id'].silence.$get({ param: { id: projectId } });
      const json = await res.json();
      if (!res.ok || !('preview' in json)) return [];
      return json.available ? json.preview : [];
    },
  });

  /**
   * 편집 락은 화면을 열 때 **딱 한 번** 잡는다(위 effect). 그 요청이 실패하면
   * — 에디터는 같은 순간에 100MB 짜리 소스를 받기 시작하므로 네트워크가 한 번
   * 흔들리기 딱 좋다 — 이후 저장이 전부 409 로 막힌다. 그런데 다시 잡는 경로가
   * 없어서, 화면에는 「저장하지 못했어요」만 뜨고 몇 번을 눌러도 결과가 같다.
   * 한 시간을 편집하고도 아무것도 못 남기는 길이다(2026-09-11 점검 H5).
   *
   * 그래서 409 를 만나면 락을 한 번 다시 잡고 그 요청만 재시도한다. 서버의
   * `claimLock` 은 주인에게 멱등이라(같은 streamerId 면 그대로 재획득) 남의
   * 편집을 뺏지 않는다 — 진짜로 남이 쥐고 있으면 재획득이 실패하고 원래 409 가
   * 그대로 올라간다.
   */
  const withLockRetry = useCallback(
    async <T extends { status: number }>(run: () => Promise<T>): Promise<T> => {
      const res = await run();
      if (res.status !== 409) return res;
      const relock = await client.editor.projects[':id'].lock
        .$post({ param: { id: projectId } })
        .catch(() => null);
      if (!relock?.ok) return res;
      return run();
    },
    [projectId]
  );

  const saveParams = useCallback(
    async (next: ShortsParams): Promise<Edl | void> => {
      // 낙관적 반영 — 칩을 눌렀는데 왕복만큼 안 눌리는 것처럼 보이면 두 번 누른다.
      queryClient.setQueryData(['project-params', projectId], next);
      const res = await withLockRetry(() =>
        client.editor.projects[':id'].params.$put({ param: { id: projectId }, json: next })
      );
      if (!res.ok) {
        // 서버가 거절하면 되돌린다. 화면만 바뀌고 서버는 옛 값인 상태를 남기지 않는다.
        void queryClient.invalidateQueries({ queryKey: ['project-params', projectId] });
        /*
         * 🔴 되돌리기만 하고 **말은 해야 한다.** 예전에는 조용히 옛 값으로 돌아가서,
         * 범위 밖 길이를 넣은 사람은 «왜 안 되지» 하고 다시 넣어 보게 됐다
         * (2026-09-11: 0·9999 를 넣으면 400 인데 화면은 아무 반응이 없었다).
         */
        const json = await res.json().catch(() => null);
        toast(
          apiErrorMessage(
            res.status,
            json,
            `설정을 저장하지 못했어요. 길이는 ${TARGET_DURATION_MIN_SEC}~${TARGET_DURATION_MAX_SEC}초 사이로 넣어 주세요.`
          ),
          'error'
        );
        return;
      }
      // 자막을 껐다 켜면 서버가 EDL 의 자막까지 맞춰서 돌려준다 — 저장(PATCH)과 같은 규약.
      const json = await res.json();
      takeRev(json);
      if ('edl' in json && json.edl) return parseEdl(json.edl);
    },
    [projectId, queryClient, toast, withLockRetry, takeRev]
  );

  /** 동작 응답에서 커맨드만 꺼낸다 — 세 라우트가 같은 모양을 돌려준다. */
  const commandsOf = (json: unknown): EditCommand[] => {
    const o = json as { commands?: unknown } | null;
    return Array.isArray(o?.commands) ? (o.commands as EditCommand[]) : [];
  };

  /**
   * 서버가 EDL 을 고친 응답 처리 — 리비전을 기록하고 커맨드를 꺼낸다.
   * 네 라우트(빈 곳 자르기·길이 맞추기·맛보기·얼굴 얹기)가 전부 이걸 쓴다.
   * 한 군데로 모은 이유는 위 주석 그대로다: 빠뜨리기 쉬운 일을 한 번만 하게 만든다.
   */
  const editResult = useCallback(
    (json: unknown): EditCommand[] => {
      takeRev(json);
      return commandsOf(json);
    },
    [takeRev]
  );

  // Acquire the single-editor lock for the session; release on unmount.
  useEffect(() => {
    if (!projectId || !hasToken()) return;
    void client.editor.projects[':id'].lock.$post({ param: { id: projectId } });
    return () => {
      void client.editor.projects[':id'].lock.$delete({ param: { id: projectId } });
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [projectId]);

  const onSave = useCallback(
    async (edl: Edl) => {
      setSaving(true);
      try {
        const res = await withLockRetry(() =>
          client.editor.projects[':id'].$patch({
            param: { id: projectId },
            // 이 편집이 바탕으로 삼은 서버 리비전. 그 사이 다른 창이 저장했으면 412 다.
            json: { edl, ...(revRef.current ? { baseRev: revRef.current } : {}) },
          })
        );
        if (!res.ok) {
          const json = await res.json();
          /*
           * 412 = 내가 불러온 뒤로 서버가 바뀌었다(다른 창·다른 기기에서 저장).
           * 서버는 덮어쓰지 않았고, 지금 화면의 편집도 그대로 남아 있다 —
           * 사라진 것이 없다는 점이 이 문구가 해야 할 일이다.
           */
          if (res.status === 412) {
            throw new Error(
              '다른 창에서 이 프로젝트를 먼저 저장했어요. 새로고침한 뒤 다시 편집해 주세요.'
            );
          }
          throw new Error(apiErrorMessage(res.status, json, '저장하지 못했어요.'));
        }
        // 컷이 바뀌었으면 서버가 발화 자막을 다시 깔아 EDL을 돌려준다 — 그대로 반영한다.
        const json = await res.json();
        takeRev(json);
        if ('edl' in json && json.edl) return parseEdl(json.edl);
      } finally {
        setSaving(false);
      }
    },
    [projectId, withLockRetry, takeRev]
  );

  const pollExport = useCallback(() => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const res = await client.editor.projects[':id'].export.$get({ param: { id: projectId } });
      const json = await res.json();
      if (res.ok && 'status' in json) {
        setExportStatus(EXPORT_LABEL[json.status] ?? null);
        if (json.status !== 'rendering' && pollRef.current) {
          window.clearInterval(pollRef.current);
        }
      }
    }, 3000);
  }, [projectId]);

  const onExport = useCallback(async () => {
    const res = await client.editor.projects[':id'].export.$post({ param: { id: projectId } });
    if (!res.ok) {
      const json = await res.json();
      // 서버가 왜 막았는지 그대로 보여준다. "시작 실패" 네 글자만 띄우면 사용자는
      // 무엇을 고쳐야 하는지 알 수 없고 타임라인에서 빠져나갈 방법도 없다.
      if ('problems' in json && Array.isArray(json.problems) && json.problems.length > 0) {
        setExportStatus(`내보낼 수 없음 · ${describeProblem(json.problems[0])}`);
        return;
      }
      setExportStatus(
        'error' in json && json.error === 'already rendering' ? '이미 렌더 중' : '시작 실패'
      );
      return;
    }
    // 선점이 서버의 updatedAt 을 바꿨다 — 탭이 안 닫히는 경우를 대비해 리비전을 맞춘다.
    takeRev(await res.json().catch(() => null));
    setExportStatus(EXPORT_LABEL.rendering!);
    // The render continues server-side (ECS), so this tab is done: hand the
    // dashboard the baton, release the edit lock, and close. Polling stays wired
    // for the case where the browser refuses to close the tab.
    notifyExportStarted(projectId);
    await client.editor.projects[':id'].lock
      .$delete({ param: { id: projectId } })
      .catch(() => undefined);
    window.close();
    pollExport();
  }, [projectId, pollExport, takeRev]);

  const clearChat = useCallback(async () => {
    const res = await client.editor.projects[':id'].chat.$delete({ param: { id: projectId } });
    if (!res.ok) throw new Error('대화 기록을 지우지 못했습니다');
    // Keep the cached project in step so re-entering the editor doesn't restore
    // the transcript we just dropped.
    queryClient.setQueryData(['project', projectId], (old?: ProjectData) =>
      old ? { ...old, chat: [] } : old
    );
  }, [projectId, queryClient]);

  const savedChat = projectQuery.data?.chat;
  const chat: AgentChatApi = useMemo(
    () => ({
      send: (messages, edl, onEvent, signal) =>
        streamAgentChat(
          projectId,
          { messages, edl },
          // 턴이 끝나면 서버가 EDL 을 쓴다 — 그 리비전을 받아 둬야 다음 수동 저장이
          // 자기가 만든 변경 때문에 412 로 막히지 않는다.
          (ev) => {
            if (ev.type === 'done') takeRev(ev);
            onEvent(ev);
          },
          signal
        ),
      history: savedChat,
      clear: clearChat,
    }),
    [projectId, savedChat, clearChat, takeRev]
  );

  const gifSearch = useCallback<GifSearch>(async (query) => {
    const res = await client.editor.gifs.search.$get({
      query: { q: query, limit: '24' },
    });
    const json = await res.json();
    if (!res.ok || !('results' in json)) {
      throw new Error('error' in json ? String(json.error) : 'GIF 검색 실패');
    }
    return json.results;
  }, []);

  const settings = useMemo(
    () => ({
      params: paramsQuery.data ?? DEFAULT_SHORTS_PARAMS,
      onParamsChange: saveParams,
      silencePreview: silenceQuery.data ?? null,
      webcamConfigured: webcamQuery.data ?? false,
      vocabulary: {
        load: async () => {
          const res = await client.editor.vocabulary.$get();
          const json = await res.json();
          if (!res.ok || !('terms' in json)) throw new Error('vocabulary load failed');
          return {
            terms: json.terms.map((t) => t.term),
            maxTerms: json.maxTerms,
            maxTermLength: json.maxTermLength,
          };
        },
        save: async (terms: string[]) => {
          const res = await client.editor.vocabulary.$put({
            json: { terms: terms.map((term) => ({ term })) },
          });
          if (!res.ok) throw new Error('vocabulary save failed');
        },
      },
      applySilence: async (edl: Edl) => {
        const mode = paramsQuery.data?.removeSilence;
        if (!mode || mode === 'off') return [];
        const res = await client.editor.projects[':id'].silence.$post({
          param: { id: projectId },
          json: { edl, mode },
        });
        const json = await res.json();
        if (!res.ok) throw new Error('빈 곳을 잘라내지 못했어요');
        // 컷이 바뀌면 남은 빈 곳도 달라진다 — 미리보기 숫자를 다시 받는다.
        void queryClient.invalidateQueries({ queryKey: ['project-silence', projectId] });
        return editResult(json);
      },
      applyLength: async (edl: Edl) => {
        const res = await client.editor.projects[':id'].recut.$post({
          param: { id: projectId },
          json: { edl },
        });
        const json = await res.json();
        if (!res.ok) throw new Error('길이를 맞추지 못했어요');
        return editResult(json);
      },
      downloadSource: () => {
        // 세로로 자르기 전 원본 비율 조각. 하이라이트 클립은 -c copy 로 잘려 16:9 그대로다.
        void (async () => {
          // 소스 키는 EDL 의 첫 영상 클립이 갖고 있다 — 프로젝트당 하이라이트는 하나다.
          const key = projectQuery.data?.edl.tracks
            .find((t) => t.kind === 'video')
            ?.items.find((i) => i.type === 'clip')?.source.assetId;
          if (!key) return;
          const res = await client.editor.assets.proxy.$get({ query: { key } });
          const json = await res.json();
          if (!res.ok || !('url' in json)) return;
          window.open(json.url, '_blank', 'noopener');
        })();
      },
      applyColdOpen: async (edl: Edl) => {
        const res = await client.editor.projects[':id']['cold-open'].$post({
          param: { id: projectId },
          json: { edl },
        });
        const json = await res.json();
        if (!res.ok) throw new Error('맛보기를 붙이지 못했어요');
        return editResult(json);
      },
      applyWebcamPip: async (edl: Edl) => {
        const res = await client.editor.projects[':id']['webcam-pip'].$post({
          param: { id: projectId },
          json: { edl, placement: 'top-right' as const },
        });
        const json = await res.json();
        if (!res.ok) throw new Error('얼굴을 얹지 못했어요');
        return editResult(json);
      },
      hasBeats: projectQuery.data?.hasBeats ?? false,
      captionAudit: projectQuery.data?.captionAudit ?? null,
      downloadSrt: (timebase: 'timeline' | 'source') => {
        // 토큰이 필요한 엔드포인트라 새 탭이 아니라 fetch → blob 으로 받는다.
        void (async () => {
          const res = await client.editor.projects[':id']['captions.srt'].$get({
            param: { id: projectId },
            query: timebase === 'source' ? { timebase: 'source' } : {},
          });
          if (!res.ok) return;
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectQuery.data?.title || 'captions'}${timebase === 'source' ? '-원본기준' : ''}.srt`;
          a.click();
          URL.revokeObjectURL(url);
        })();
      },
    }),
    [
      paramsQuery.data,
      saveParams,
      silenceQuery.data,
      webcamQuery.data,
      projectId,
      queryClient,
      projectQuery.data?.title,
      projectQuery.data?.edl.tracks,
      projectQuery.data?.hasBeats,
      projectQuery.data?.captionAudit,
      editResult,
    ]
  );

  if (!hasToken()) {
    return <CenterNotice text="로그인이 필요해요. 스트리머 대시보드에서 에디터를 열어 주세요." />;
  }
  if (projectQuery.isLoading) return <CenterNotice text="프로젝트 불러오는 중…" />;
  if (projectQuery.isError || !projectQuery.data) {
    /*
     * 이유를 그대로 보여준다. 예전엔 한 문장으로 뭉뚱그려서, 토큰이 만료됐을 때도
     * 「불러오지 못했습니다」만 떴다 — 사용자는 새로고침만 반복하게 된다(토큰은
     * 1시간짜리이고 여기에 갱신 로직이 없으므로 자주 만나는 화면이다).
     */
    // 단, **한국어일 때만** 그대로 보여준다 — 내부 예외 원문(zod·fetch)이 화면에
    // 새지 않게 하는 마지막 방벽이다.
    const why = humanError(
      projectQuery.error,
      '프로젝트를 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.'
    );
    return <CenterNotice text={why} />;
  }

  return (
    <EditorShell
      initialEdl={projectQuery.data.edl}
      resolver={resolver}
      title={projectQuery.data.title}
      onSave={onSave}
      onExport={onExport}
      saving={saving}
      exportStatus={exportStatus}
      agent={chat}
      settings={settings}
      gifSearch={gifSearch}
      branding={brandingQuery.data}
      sourceDurationSec={projectQuery.data?.sourceDurationSec}
    />
  );
}

function CenterNotice({ text }: { text: string }) {
  return (
    <div className="flex h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      {text}
    </div>
  );
}
